"use client";

interface ChipOption<V extends string> {
  value: V;
  label: string;
}

interface FilterChipGroupProps<V extends string> {
  label: string;
  options: ReadonlyArray<ChipOption<V>>;
  /** 選択中の値。どれも選んでいない状態を持つ行（大学専用）は null を渡す */
  value: V | null;
  onChange: (value: V) => void;
  /**
   * 選択中のチップをもう一度押したときの処理。渡すと選択を外せる行になる
   * （大学専用の行は「何も押していない＝通常の質問と一緒に表示」を既定にするため / #1861）
   */
  onDeselect?: () => void;
  // カテゴリ専用の横スクロール挙動。デフォルトは折り返し（flex-wrap）
  scrollable?: boolean;
}

export default function FilterChipGroup<V extends string>({
  label,
  options,
  value,
  onChange,
  onDeselect,
  scrollable = false,
}: FilterChipGroupProps<V>) {
  const wrapperClass = scrollable
    ? "flex gap-2 flex-nowrap overflow-x-auto pb-1 -mx-1 px-1"
    : "flex gap-2 flex-wrap";

  return (
    <div className="mb-3">
      <p className="text-xs font-semibold tracking-wider mb-1.5">{label}</p>
      <div className={wrapperClass} role="group" aria-label={label}>
        {options.map((option) => {
          const selected = value === option.value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => (selected && onDeselect ? onDeselect() : onChange(option.value))}
              aria-pressed={selected}
              className={`shrink-0 text-xs px-3.5 py-2 min-h-[36px] rounded-full border-[1.5px] transition-[color,background-color,border-color,opacity] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface ${
                selected
                  ? "bg-primary text-white border-primary font-bold"
                  : "bg-white text-primary border-primary opacity-50 hover:opacity-80"
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
